import { format, parseISO } from 'date-fns';
import { de, enUS, fr } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale as ELocale } from '@/helpers/languages';
import { useCMSConfig } from '@/modules/common';
import {
  END_DATE_FORMAT_MAP,
  getCurrentBonanzaIterationDays,
} from '@/modules/promotions/helpers';
import {
  PERCENT_BREAKPOINTS,
  type TProgressBreakpoint,
} from '@/modules/promotions/helpers/common';
import { useBonanzaPromo } from '@/modules/promotions/hooks';
import { useGetCommonStore } from '@/store';
import { ScCoin, ScProgressBar, ScProgressBarContainer } from './styled';

const ZERO_PROGRESS = '52px';
const ZERO_PROGRESS_MOBILE = '42px';

// TODO: create DateService to remove code duplication (DEV-866)
export const LOCALE_MAP: Record<string, Locale> = {
  [ELocale.GERMAN]: de,
  [ELocale.FRENCH_CANADA]: fr,
  [ELocale.FRENCH_SWITZERLAND]: fr,
};

export const ProgressBar = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('bonanzaPromo');
  const useCommonStore = useGetCommonStore();

  const bonanzaProgressPercent = useCommonStore.use.bonanzaProgressPercent();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { isLastRound } = useBonanzaPromo();
  const locale = LOCALE_MAP[language] || enUS;
  const { cmsConfig } = useCMSConfig();
  const endDateFormat = END_DATE_FORMAT_MAP[language] ?? 'MMM d';

  const endDate =
    cmsConfig?.bonanzaPromo.dateRange?.end &&
    format(parseISO(cmsConfig.bonanzaPromo.dateRange.end), endDateFormat, {
      locale,
    });

  const currentIterationDays = useMemo(() => {
    const startDate = cmsConfig?.bonanzaPromo.dateRange?.start;
    if (!startDate) return null;

    return getCurrentBonanzaIterationDays(startDate, locale);
  }, [cmsConfig?.bonanzaPromo.dateRange?.start, locale]);

  const indexOfCurrentUserLevel = useMemo(
    () =>
      PERCENT_BREAKPOINTS.indexOf(
        bonanzaProgressPercent as TProgressBreakpoint
      ),
    [bonanzaProgressPercent]
  );

  const progress = useMemo(() => {
    const progressPercent = PERCENT_BREAKPOINTS[indexOfCurrentUserLevel];

    const zeroProgress = isMobileDevice ? ZERO_PROGRESS_MOBILE : ZERO_PROGRESS;

    return progressPercent
      ? `${PERCENT_BREAKPOINTS[indexOfCurrentUserLevel]}%`
      : zeroProgress;
  }, [indexOfCurrentUserLevel, isMobileDevice]);

  return (
    <ScProgressBarContainer>
      {currentIterationDays && (
        <div className="deadline-starts">
          <strong>{currentIterationDays.startDay}</strong>
          <span> - {t('progress.starts')}</span>
        </div>
      )}
      <ScProgressBar progress={progress}>
        <div className="progress-line">
          {PERCENT_BREAKPOINTS.map((breakpoint, index) => (
            <div className="breakpoint" key={breakpoint}>
              <ScCoin
                mode={index <= indexOfCurrentUserLevel ? 'passed' : 'future'}
              >
                <p>
                  {breakpoint}
                  <span>%</span>
                </p>
              </ScCoin>
            </div>
          ))}
        </div>
      </ScProgressBar>
      <div className="deadline-finishes">
        {isLastRound && (
          <strong className="deadline-ends">{t('progress.ends')}</strong>
        )}
        <strong>
          {isLastRound
            ? currentIterationDays?.endDayShort
            : currentIterationDays?.endDay}
        </strong>
        {isLastRound && endDate ? (
          <span> ({endDate})</span>
        ) : (
          <span> - {t('progress.finishes')}</span>
        )}
      </div>
    </ScProgressBarContainer>
  );
};
